import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/apiClient";
import { Loader2, Users, UserCheck, UserX, FolderKanban, CheckCircle2, TrendingUp, Zap, Mail, Search, Sparkles, Receipt } from "lucide-react";

function useManagerGuard() {
  const { identity } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    if (identity && !identity.roles?.includes("MANAGER")) navigate("/become-manager");
  }, [identity, navigate]);
}

const CARDS = [
  { key: "totalArtists", label: "Membres", icon: Users, color: "text-amber-400" },
  { key: "activeArtists", label: "Actifs", icon: UserCheck, color: "text-emerald-400" },
  { key: "inactiveArtists", label: "Inactifs", icon: UserX, color: "text-zinc-400" },
  { key: "projectsInProgress", label: "Projets en cours", icon: FolderKanban, color: "text-blue-400" },
  { key: "projectsDone", label: "Projets terminés", icon: CheckCircle2, color: "text-emerald-400" },
  { key: "missionsToValidate", label: "Missions à valider", icon: CheckCircle2, color: "text-orange-400" },
  { key: "globalProgression", label: "Progression globale", icon: TrendingUp, color: "text-amber-400", suffix: "" },
  { key: "totalXp", label: "XP générés", icon: Zap, color: "text-amber-400" },
];

export default function ManagerDashboard() {
  useManagerGuard();
  const [d, setD] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { api.get("/manager/dashboard").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 text-amber-500 animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="manager-dashboard">
      <div>
        <h1 className="font-display text-3xl font-extrabold">Tableau de bord Manager</h1>
        <p className="text-zinc-500 text-sm mt-1">Pilote et accompagne tes membres.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="manager-quick">
        <button onClick={() => navigate("/manager/discover")} data-testid="quick-discover" className="text-left bg-[#18181E] border border-zinc-800 rounded-2xl p-4 card-hover">
          <Search className="w-5 h-5 text-amber-400 mb-2" /><div className="font-semibold text-zinc-100">Découvrir des artistes</div><div className="text-xs text-zinc-500 mt-0.5">Trouve de nouveaux talents à accompagner</div>
        </button>
        <button onClick={() => navigate("/manager/matching")} data-testid="quick-matching" className="text-left bg-[#18181E] border border-zinc-800 rounded-2xl p-4 card-hover">
          <Sparkles className="w-5 h-5 text-amber-400 mb-2" /><div className="font-semibold text-zinc-100">Matching IA</div><div className="text-xs text-zinc-500 mt-0.5">Décris ton besoin, l'IA classe les artistes</div>
        </button>
        <button onClick={() => navigate("/manager/invitations")} data-testid="quick-invitations" className="text-left bg-[#18181E] border border-zinc-800 rounded-2xl p-4 card-hover">
          <Mail className="w-5 h-5 text-amber-400 mb-2" /><div className="font-semibold text-zinc-100">Invitations</div><div className="text-xs text-zinc-500 mt-0.5">Suis les invitations envoyées</div>
        </button>
        <button onClick={() => navigate("/manager/commerce")} data-testid="quick-commerce" className="text-left bg-[#18181E] border border-zinc-800 rounded-2xl p-4 card-hover">
          <Receipt className="w-5 h-5 text-amber-400 mb-2" /><div className="font-semibold text-zinc-100">Commercial</div><div className="text-xs text-zinc-500 mt-0.5">Devis, commandes et factures de tes artistes</div>
        </button>
      </div>

      {d.totalArtists === 0 ? (
        <div className="bg-[#18181E] border border-zinc-800 rounded-3xl p-8 text-center" data-testid="manager-empty">
          <Users className="w-10 h-10 text-amber-500 mx-auto mb-3" />
          <p className="text-zinc-300 font-semibold">Aucun membre pour l'instant</p>
          <p className="text-zinc-500 text-sm mt-1 mb-4">Invite ton premier membre pour commencer à l'accompagner.</p>
          <button onClick={() => navigate("/manager/invitations")} data-testid="empty-invite-btn"
            className="inline-flex items-center gap-2 px-5 h-11 rounded-xl gold-gradient text-black font-semibold"><Mail className="w-4 h-4" /> Inviter un membre</button>
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="manager-stats">
          {CARDS.map((c) => (
            <div key={c.key} className="bg-[#18181E] border border-zinc-800 rounded-2xl p-4" data-testid={`stat-${c.key}`}>
              <c.icon className={`w-5 h-5 ${c.color} mb-2`} />
              <div className="font-display text-2xl font-extrabold">{d[c.key] ?? 0}</div>
              <div className="text-xs text-zinc-500 mt-0.5">{c.label}</div>
            </div>
          ))}
        </div>
      )}

      {d.recentActivity?.length > 0 && (
        <div className="bg-[#18181E] border border-zinc-800 rounded-3xl p-5" data-testid="manager-recent">
          <h2 className="font-display text-lg font-bold mb-3">Activité récente</h2>
          <div className="space-y-2">
            {d.recentActivity.map((a, i) => (
              <div key={i} className="flex items-center justify-between text-sm border-b border-zinc-800/60 last:border-0 pb-2">
                <span className="text-zinc-300">{a.artist}</span>
                <span className="text-zinc-500 truncate mx-3 flex-1 text-center">{a.label}</span>
                <span className="text-amber-400 font-mono shrink-0">+{a.xp} XP</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
